import React, { useRef, useState } from 'react';
import { Plus, X, FileUp, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { productsAPI } from '../services/api';

const normalize = (v) => v.replace(/[^a-z0-9]/gi, '').toLowerCase();

// Accepte « REF », « REF1, REF2 » ou une liste multi-lignes « MARQUE : REF1, REF2 » → [{ ref, brand }]
export const parseRefs = (text) => (text || '').split(/[\r\n|]+/).flatMap((line) => {
  const l = line.trim();
  if (!l) return [];
  const idx = l.search(/[:\t]/);
  const brand = idx >= 0 && l.slice(idx + 1).trim() ? l.slice(0, idx).trim().toUpperCase() : null;
  const body = brand ? l.slice(idx + 1) : l;
  return body.split(/[,;]+/).map(p => p.trim()).filter(Boolean).map(ref => ({ ref, brand }));
});

const RefListInput = ({ label, hint, values, brands = {}, onChange, exclude = [], testId, placeholder, max = 300 }) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const addMany = (items) => {
    if (!items.length) return;
    if (values.length >= max) { setError(`${max} références maximum`); return; }
    const taken = new Set([...values, ...exclude].map(normalize));
    const fresh = [];
    const nextBrands = { ...brands };
    for (const { ref, brand } of items) {
      const n = normalize(ref);
      if (!n) continue;
      if (taken.has(n)) {
        const existing = values.find(v => normalize(v) === n);
        if (existing && brand && !nextBrands[existing]) nextBrands[existing] = brand;
        continue;
      }
      taken.add(n);
      fresh.push(ref);
      if (brand) nextBrands[ref] = brand;
    }
    if (!fresh.length) { setError(items.length > 1 ? 'Références déjà présentes' : 'Référence déjà présente'); onChange(values, nextBrands); return; }
    const kept = fresh.slice(0, max - values.length);
    setError(kept.length < fresh.length ? `${max} références maximum : ${fresh.length - kept.length} ignorée(s)` : '');
    const withBrand = kept.filter(r => nextBrands[r]).length;
    setInfo(kept.length > 1 ? `${kept.length} références ajoutées${withBrand ? ` (${withBrand} avec marque)` : ''}` : '');
    onChange([...values, ...kept], nextBrands);
    setInput('');
  };

  const add = () => addMany(parseRefs(input));

  const onPaste = (e) => {
    const text = e.clipboardData?.getData('text') || '';
    if (!/[\n:|]/.test(text)) return;
    e.preventDefault();
    addMany(parseRefs(text));
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setLoading(true); setError(''); setInfo('');
    try {
      const d = await productsAPI.parseRefsFile(file);
      if (!d.count) setError('Aucune référence trouvée dans ce fichier');
      else addMany(d.items);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Fichier illisible');
    } finally { setLoading(false); }
  };

  const remove = (idx) => {
    const v = values[idx];
    const next = { ...brands }; delete next[v];
    onChange(values.filter((_, i) => i !== idx), next);
  };

  return (
    <div data-testid={`${testId}-field`}>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label} {values.length > 0 && <span className="text-gray-400" data-testid={`${testId}-count`}>({values.length}/{max})</span>}</label>
      <div className="flex gap-2">
        <Input
          data-testid={`${testId}-input`}
          value={input}
          onChange={(e) => { setInput(e.target.value); setError(''); setInfo(''); }}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          onPaste={onPaste}
          placeholder={placeholder}
          className="font-mono"
        />
        <Button type="button" variant="outline" data-testid={`${testId}-add`} onClick={add}><Plus size={16} /></Button>
        <Button type="button" variant="outline" data-testid={`${testId}-file`} title="Importer une liste depuis un fichier PDF, CSV, XLSX ou TXT" onClick={() => fileRef.current?.click()} disabled={loading}>
          {loading ? <Loader2 size={16} className="animate-spin" /> : <FileUp size={16} />}
        </Button>
        <input ref={fileRef} type="file" accept=".pdf,.csv,.xlsx,.txt" className="hidden" onChange={onFile} data-testid={`${testId}-file-input`} />
      </div>
      {hint && <p className="text-[11px] text-gray-400 mt-1">{hint} Collez une liste « Marque : réf1, réf2 » (une marque par ligne) ou importez un fichier PDF/CSV/TXT : la marque est conservée à côté de chaque référence.</p>}
      {error && <p className="text-xs text-red-600 mt-1" data-testid={`${testId}-error`}>{error}</p>}
      {info && !error && <p className="text-xs text-green-700 mt-1" data-testid={`${testId}-info`}>{info}</p>}
      <div className="flex flex-wrap gap-1.5 mt-2" data-testid={`${testId}-list`}>
        {values.map((v, idx) => (
          <span key={v} data-testid={`${testId}-chip-${idx}`} title={brands[v] ? `${brands[v]} : ${v}` : v} className="inline-flex items-center gap-1 text-xs font-mono bg-blue-50 text-blue-800 border border-blue-200 px-2 py-1 rounded-full">
            {brands[v] && <span className="font-sans text-[10px] font-semibold text-blue-500 uppercase" data-testid={`${testId}-chip-brand-${idx}`}>{brands[v]}</span>}
            {v}
            <button type="button" data-testid={`${testId}-remove-${idx}`} onClick={() => remove(idx)} className="hover:text-red-600"><X size={12} /></button>
          </span>
        ))}
      </div>
    </div>
  );
};

export default RefListInput;
